import React from "react";
import { Link } from "react-router-dom";

export default function SmsConsent() {
  const sectionStyle = { marginBottom: 28 };
  const h2Style = { fontSize: 20, fontWeight: 800, marginBottom: 10, color: "#1a1a1a" };
  const pStyle = { fontSize: 15, lineHeight: 1.7, color: "#444", marginBottom: 10 };
  const ulStyle = { paddingLeft: 24, marginBottom: 10 };
  const liStyle = { fontSize: 15, lineHeight: 1.7, color: "#444", marginBottom: 6 };
  const boxStyle = {
    padding: 20, borderRadius: 12, border: "1px solid #fecaca", background: "#fef2f2", marginBottom: 20
  };

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 20px" }}>
      <div style={{ marginBottom: 30 }}>
        <Link to="/" style={{ color: "#dc2626", textDecoration: "none", fontWeight: 700, fontSize: 20 }}>
          RoadOptix
        </Link>
      </div>

      <h1 style={{ fontSize: 32, fontWeight: 900, marginBottom: 8, color: "#111" }}>SMS Terms & Conditions</h1>
      <p style={{ fontSize: 14, color: "#888", marginBottom: 30 }}>Last Updated: February 26, 2026</p>

      <div style={boxStyle}>
        <p style={{ fontSize: 16, fontWeight: 700, color: "#991b1b", marginBottom: 8 }}>
          SMS Consent Summary
        </p>
        <p style={{ fontSize: 15, color: "#7f1d1d", lineHeight: 1.6 }}>
          By providing your phone number on the RoadOptix platform, you consent to receive automated 
          SMS/text messages related to freight operations, load alerts, and account notifications. 
          Message frequency varies. Msg & data rates may apply. Reply STOP to cancel. Reply HELP for help.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>1. Program Description</h2>
        <p style={pStyle}>
          RoadOptix, operated by Symple Code Business Systems, LLC, provides an SMS notification 
          service for registered carriers, brokers, and platform users. Our SMS program delivers 
          time-sensitive logistics notifications to help you manage your freight operations efficiently.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>2. Types of Messages</h2>
        <p style={pStyle}>You may receive the following types of SMS messages:</p>
        <ul style={ulStyle}>
          <li style={liStyle}><strong>Backhaul Load Alerts:</strong> AI-matched return load opportunities near your delivery location</li>
          <li style={liStyle}><strong>Load Status Updates:</strong> Notifications when loads are tendered, accepted, picked up, or delivered</li>
          <li style={liStyle}><strong>Bid Notifications:</strong> Alerts when your bid is awarded or when new bids are received on your loads</li>
          <li style={liStyle}><strong>Account Alerts:</strong> Security notifications, compliance reminders, and payment updates</li>
          <li style={liStyle}><strong>Operational Updates:</strong> Schedule changes, dispatch notifications, and urgent communications</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>3. Opt-In / Consent</h2>
        <p style={pStyle}>
          You opt in to receive SMS messages from RoadOptix when you:
        </p>
        <ul style={ulStyle}>
          <li style={liStyle}>Provide your phone number during account registration or carrier onboarding</li>
          <li style={liStyle}>Add or update your phone number on your carrier profile page</li>
          <li style={liStyle}>Enable SMS notifications in your account settings</li>
        </ul>
        <p style={pStyle}>
          <strong>Your consent is not a condition of purchase or use of the platform.</strong> You can 
          use RoadOptix without opting in to SMS notifications.
        </p>
        <p style={pStyle}>
          <strong>No mobile information will be shared with third parties/affiliates for 
          marketing/promotional purposes.</strong> All categories listed above exclude text messaging 
          originator opt-in data and consent; this information will not be shared with any third parties.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>4. Message Frequency</h2>
        <p style={pStyle}>
          Message frequency varies based on your activity on the platform, the number of loads you're 
          involved with, and the availability of matching loads. Active carriers may receive several 
          messages per day during busy periods. You can control notification frequency through your 
          profile settings.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>5. Message & Data Rates</h2>
        <p style={pStyle}>
          Standard message and data rates may apply depending on your mobile carrier and plan. 
          RoadOptix does not charge for SMS messages, but your wireless carrier may charge you 
          for each message received.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>6. How to Opt Out</h2>
        <p style={pStyle}>
          You can stop receiving SMS messages at any time by:
        </p>
        <ul style={ulStyle}>
          <li style={liStyle}><strong>Replying STOP</strong> to any message from RoadOptix</li>
          <li style={liStyle}>Removing your phone number from your carrier profile</li>
          <li style={liStyle}>Contacting us at support@roadoptix.com</li>
        </ul>
        <p style={pStyle}>
          After opting out, you will receive one final confirmation message. You will no longer receive 
          SMS notifications but can continue to use the platform through the web interface.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>7. How to Get Help</h2>
        <p style={pStyle}>
          For help with SMS messaging, you can:
        </p>
        <ul style={ulStyle}>
          <li style={liStyle}><strong>Reply HELP</strong> to any message for assistance</li>
          <li style={liStyle}>Email us at support@roadoptix.com</li>
          <li style={liStyle}>Call us at (346) 224-0790</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>8. Supported Carriers</h2>
        <p style={pStyle}>
          SMS messages are supported on all major US wireless carriers including AT&T, Verizon, 
          T-Mobile, Sprint, and most regional carriers. Carriers are not liable for delayed or 
          undelivered messages.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>9. Privacy</h2>
        <p style={pStyle}>
          Your phone number and SMS data are protected under our{" "}
          <Link to="/privacy" style={{ color: "#dc2626" }}>Privacy Policy</Link>. We do not sell, 
          rent, or share your phone number with third parties for marketing purposes. Phone numbers 
          are used solely for delivering Service-related notifications through the RoadOptix platform.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={h2Style}>10. Contact Information</h2>
        <p style={pStyle}>
          <strong>Symple Code Business Systems, LLC</strong><br />
          Houston, TX<br />
          Email: support@roadoptix.com<br />
          Phone: (346) 224-0790
        </p>
      </div>

      <div style={{ borderTop: "1px solid #eee", paddingTop: 20, marginTop: 40, fontSize: 13, color: "#888" }}>
        <Link to="/terms" style={{ color: "#888", marginRight: 20 }}>Terms of Service</Link>
        <Link to="/sms-consent" style={{ color: "#888", marginRight: 20 }}>SMS Terms</Link>
        <Link to="/privacy" style={{ color: "#888" }}>Privacy Policy</Link>
      </div>
    </div>
  );
}
